/**
 * What the wallet does with the node and the SDK on behalf of the views: the contract catalogue, the
 * checks that turn a form into a pending transfer or call, and building, signing and submitting it.
 *
 * The view models call these functions and hold no crypto and no API client. The chain id, the protocol
 * version and the sequence are read from the node at submission, never from the read API, which can be a
 * block or two behind: a quick second send built on its sequence would reuse a sequence and be rejected.
 */

import { buildTransferTx, buildContractCallTx, signTransaction, txToString, validateAmount, validateAddress, isContractAddress } from 'streembit-bc-sdk';
import { SBRIT_FEE_AMOUNT, AssetId, ContractLocation } from 'streembit-bc-types';
import { apiBaseUrl, fetchStatus, fetchAccount, fetchContract, submitTransaction, ApiError } from 'streembit-bc-sdk/api';
import type { SubmitResult } from 'streembit-bc-sdk/api';
import { requireSession, withSigner } from './session';

export const FEE = SBRIT_FEE_AMOUNT;
const SBRIT_DECIMALS = 8;

export interface ContractMethod {
    name: string;
    caller: string;
    effect: string;
}

/** A deployed contract as the node's catalogue lists it. */
export interface ContractInfo {
    cid: string;
    title: string;
    contractName: string;
    methods: ContractMethod[];
}

export interface PendingTransfer {
    to: string;
    amount: string;
}

export interface PendingCall {
    contract: ContractInfo;
    cid: string;
    method: string;
    params: Record<string, unknown> | undefined;
    amount: string;
}

// ---------------------------------------------------------------------------
// Fixed-point arithmetic on decimal strings (8 places), for balance checks and totals only.
// Transactions carry the strings the user typed; the node does the accounting.
// ---------------------------------------------------------------------------

export function toUnits(amount: string): bigint {
    const [whole, fraction = ''] = amount.split('.');
    return BigInt(whole || '0') * 10n ** BigInt(SBRIT_DECIMALS) + BigInt((fraction + '0'.repeat(SBRIT_DECIMALS)).slice(0, SBRIT_DECIMALS));
}

export function fromUnits(units: bigint): string {
    const base = 10n ** BigInt(SBRIT_DECIMALS);
    const whole = units / base;
    const fraction = (units % base).toString().padStart(SBRIT_DECIMALS, '0').replace(/0+$/, '');
    return fraction ? `${whole}.${fraction}` : `${whole}`;
}

export function addAmounts(a: string, b: string): string {
    return fromUnits(toUnits(a) + toUnits(b));
}

// ---------------------------------------------------------------------------
// Node reads
// ---------------------------------------------------------------------------

export function contractLabel(contract: ContractInfo): string {
    return contract.contractName ? `${contract.title} (${contract.contractName})` : contract.title;
}

/**
 * The contract catalogue: the deployed contracts with their method tables, from the node. The SDK's API
 * client has no function for this route yet, so this is the one request the page makes outside it; it
 * moves into streembit-bc-sdk/api when the node publishes the route.
 *
 *   GET /api/v1/contracts -> { contracts: [ { cid, title, contractName, methods: [ { name, caller, effect } ] } ] }
 */
export async function fetchContractCatalogue(): Promise<ContractInfo[]> {
    let response: Response;
    try {
        response = await fetch(`${apiBaseUrl()}/api/v1/contracts`, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000) });
    } catch (err) {
        throw new ApiError(`The node API at ${apiBaseUrl()} did not answer: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!response.ok) {
        // The node's error shape, { error, message, details }, read the way the SDK's client reads it.
        const failure = await response.json().catch(() => null) as { error?: unknown; message?: unknown; details?: unknown } | null;
        const code = failure && typeof failure.error === 'string' ? failure.error : '';
        const text = failure && typeof failure.message === 'string' && failure.message ? failure.message : code || `HTTP ${response.status}`;
        throw new ApiError(text, response.status, code, failure?.details);
    }
    const body = await response.json() as { contracts?: unknown };
    if (!Array.isArray(body.contracts)) throw new Error('The contract catalogue has no contracts list');
    return body.contracts.map((entry: unknown) => {
        const c = entry as Partial<ContractInfo>;
        if (typeof c.cid !== 'string' || typeof c.title !== 'string' || !Array.isArray(c.methods)) throw new Error('A contract in the catalogue lacks cid, title or methods');
        if (!isContractAddress(c.cid)) throw new Error(`The node's catalogue lists an invalid contract address ${c.cid}`);
        return { cid: c.cid, title: c.title, contractName: typeof c.contractName === 'string' ? c.contractName : '', methods: c.methods };
    });
}

// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------

export interface TransferForm {
    to: string;
    amount: string;
    /** the balance shown to the user, for the early check; the node's balance is checked again at submission */
    balance: string | null;
}

export function reviewTransfer(form: TransferForm): PendingTransfer {
    const s = requireSession();
    const to = form.to.trim();
    const amount = form.amount.trim();
    if (!validateAddress(to)) throw new Error('The recipient address is not a valid Streembit address');
    if (to === s.account.address) throw new Error('The recipient is your own address');
    if (!validateAmount(amount)) throw new Error('The amount must be a positive number with up to 8 decimal places');
    if (form.balance !== null && toUnits(addAmounts(amount, FEE)) > toUnits(form.balance)) {
        throw new Error(`The amount plus the fee exceeds the available balance of ${form.balance} SBRIT`);
    }
    return { to, amount };
}

/** Builds, signs and submits the transfer; returns the transaction id and whether the node already included it in a block. */
export async function submitTransfer(pending: PendingTransfer): Promise<SubmitResult> {
    const s = requireSession();
    const chain = await fetchStatus('node');
    const account = await fetchAccount(s.account.address, 'node');
    if (toUnits(addAmounts(pending.amount, FEE)) > toUnits(account.balance)) {
        throw new Error(`The amount plus the fee exceeds the balance of ${account.balance} SBRIT`);
    }
    const tx = buildTransferTx({ chainId: chain.chainId, version: chain.version, from: s.account.address, to: pending.to, amount: pending.amount, asset: AssetId.SBRIT, sequence: account.sequence + 1 });
    await withSigner(signer => signTransaction(tx, [signer]));
    return submitTransaction(txToString(tx));
}

// ---------------------------------------------------------------------------
// Contract calls
// ---------------------------------------------------------------------------

export interface CallForm {
    contract: ContractInfo | undefined;
    method: string;
    params: string;
    amount: string;
}

export function parseParams(text: string): Record<string, unknown> | undefined {
    const trimmed = text.trim();
    if (!trimmed) return undefined;
    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        throw new Error('The parameters are not valid JSON');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('The parameters must be a JSON object');
    }
    return Object.keys(parsed as object).length ? parsed as Record<string, unknown> : undefined;
}

export function reviewCall(form: CallForm): PendingCall {
    requireSession();
    const contract = form.contract;
    if (!contract) throw new Error('Choose a contract');
    if (!form.method) throw new Error('Choose a method');
    const params = parseParams(form.params);
    const amount = form.amount.trim() || '0';
    if (amount !== '0' && !validateAmount(amount)) throw new Error('The amount must be 0 or a positive number with up to 8 decimal places');
    return { contract, cid: contract.cid, method: form.method, params, amount };
}

/** Reads the contract's metadata from the node, builds, signs and submits the call; returns the transaction id and whether the node already included it in a block. */
export async function submitContractCall(pending: PendingCall): Promise<SubmitResult> {
    const s = requireSession();
    const chain = await fetchStatus('node');
    const meta = await fetchContract(pending.cid);
    const locations = Object.values(ContractLocation) as string[];
    if (!locations.includes(meta.location)) throw new Error(`The node reported an unknown contract location "${meta.location}"`);
    const account = await fetchAccount(s.account.address, 'node');
    if (toUnits(addAmounts(pending.amount, FEE)) > toUnits(account.balance)) {
        throw new Error(`The amount plus the fee exceeds the balance of ${account.balance} SBRIT`);
    }
    const tx = buildContractCallTx({
        chainId: chain.chainId,
        version: chain.version,
        from: s.account.address,
        to: pending.cid,
        method: pending.method,
        data: pending.params,
        codeHash: meta.codeHash,
        sandboxHash: meta.sandboxHash,
        location: meta.location as ContractLocation,
        amount: pending.amount,
        sequence: account.sequence + 1
    });
    await withSigner(signer => signTransaction(tx, [signer]));
    return submitTransaction(txToString(tx));
}
